import {Component, EventEmitter, Input, OnInit, PipeTransform} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MatDialog, MatSnackBar, MatTableDataSource, PageEvent} from '@angular/material';
import {Observable} from 'rxjs';

import {RequestResult, TableData, TableOptions} from '../../shared.model';
import { OkCancelDialogComponent } from '../ok-cancel-dialog/ok-cancel-dialog.component';

@Component({
  selector: 'iaud-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0', minHeight: '0', display: 'none'})),
      state('expanded', style({height: '*', display: 'inherit'})),
      transition('expanded <=> collapsed', animate('200ms cubic-bezier(0, .4, .8, 1)')),
    ]),
  ],
})
export class TableComponent<T> implements OnInit {
  columnFieldNames: Array<string>;
  expandedElement: number;
  newRow: T;
  dataSource: MatTableDataSource<T>;

  @Input()
  options: TableOptions;
  @Input('data')
  data: TableData<T>;
  @Input()
  dataRefreshCallback: (page: number, pageSize: number) => Observable<TableData<T>>;
  @Input()
  dataDeleteCallback: (id: number, data: T) => Observable<RequestResult>;
  @Input()
  dataEditCallback: (id: number, data: T) => void;
  @Input()
  forceReloadEvent?: EventEmitter<boolean>;
  @Input()
  deleteTitle: string = 'Delete Confirmation';
  @Input()
  deleteText: string = 'Are you sure you want to delete this row?';
  @Input()
  usePaginator: boolean = true;

  constructor(private dialog:  MatDialog, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.columnFieldNames = this.options.columns.map(column => column.fieldName);

    const parsedData = this.generateDetailRows();

    this.dataSource = new MatTableDataSource<T>(parsedData);

    if (this.forceReloadEvent)
      this.forceReloadEvent.subscribe((shouldResetPage: boolean) => {
          this.onPageChange({
          pageIndex: shouldResetPage ? 0 : this.data.page,
          pageSize: this.data.pageSize,
          length: null,
        });
      });
  }

  generateDetailRows(): Array<T> {
    if (!this.options || !this.options.additionalInfo)
      return this.data.items;

    const rows = [];

    this.data.items.forEach(item => {
      rows.push(item, {detailRow: true, item});
    });

    return rows;
  }

  // @ts-ignore
  deleteData(id: number, data: T): void {
    const dialogRef = this.dialog.open(OkCancelDialogComponent, {
      // @ts-ignore
      data: {title: eval('`'+this.deleteTitle+'`'), text: eval('`'+this.deleteText+'`'), okText: 'Delete'}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dataDeleteCallback(id, data)
          .subscribe(result => {
            if (result.success) {
              this.snackBar.open('Data deleted', 'Ok', {
                duration: 2000,
              });
              this.onPageChange({
                pageIndex: this.data.page,
                pageSize: this.data.pageSize,
                length: null,
              });
            } else {
              console.log(`error: `);
              console.dir(result.messages);
            }
          });
      }
    });
  }
  editData(id: number, data: T): void {
    this.dataEditCallback(id, data);
  }

  refreshTable(): void {
    const parsedData = this.generateDetailRows();
    this.dataSource = new MatTableDataSource<T>(parsedData);
  }

  toggleNewRow(row?) {
    if (!row) {
      if (this.newRow) {
        delete this.newRow;
        this.data.items.shift();
      }
    } else {
      if (this.newRow) {
        this.data.items.shift();
      }
      this.newRow = row;
      this.data.items.unshift(row);
    }

    this.refreshTable();
  }

  onPageChange(event: PageEvent): void {
    if (this.usePaginator) {
      this.dataRefreshCallback(event.pageIndex, event.pageSize)
      .subscribe((res: TableData<T>) => {
        this.data.pageSize = event.pageSize;
        this.data.totalCount = res.totalCount;
        this.data.page = res.current - 1;
        this.data.items = res.items;

        if (this.newRow)
          this.data.items.unshift(this.newRow);

        const parsedData = this.generateDetailRows();

        this.dataSource = new MatTableDataSource<T>(parsedData);

      });
    } else {
      this.dataRefreshCallback(0, 0)
      .subscribe((res: TableData<T>) => {
        this.data.pageSize = event.pageSize;
        this.data.totalCount = res.totalCount;
        this.data.page = res.current - 1;
        this.data.items = res.items;

        if (this.newRow)
          this.data.items.unshift(this.newRow);
          
        const parsedData = this.generateDetailRows();

        this.dataSource = new MatTableDataSource<T>(parsedData);
        this.toggleNewRow(this.newRow);
      });
    }
  }

  toggleExpandRow(row): number {
    if (row.id === this.expandedElement)
      return this.expandedElement = null;

    return this.expandedElement = row.id;
  }

  getPipedValue(value: string, pipe: PipeTransform, pipeParams?: string): string {
    return pipe.transform(value, pipeParams);
  }

  isExpansionDetailRow(_index: number, row: T): boolean {
    return row.hasOwnProperty('detailRow');
  }
  isNewRow(row: T): boolean {
    return row === this.newRow;
  }
}
